/**
 * ExportModal — focused CV export modal.
 *
 * Lets the user pick format (PDF / DOCX / JSON / Print) and download.
 * "PDF ATS" was removed — the ATS template itself already produces
 * an ATS-compatible PDF, so a separate ATS export is redundant.
 */

import { Download, FileText, Code, Printer } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Skeleton } from '../../ui/Skeleton';
import { cn } from '@/lib/utils';
import type { CVData } from '../../../types';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  data: CVData;
  onPrint: (mode: 'graphique' | 'ats') => void;
  onExportDocx: () => Promise<void>;
  onExportJson: () => void;
  isExporting: boolean;
}

type ExportFormat = 'pdf' | 'docx' | 'json' | 'print';

const FORMATS: Array<{ id: ExportFormat; icon: React.ElementType; label: string; desc: string; color: string }> = [
  { id: 'pdf',   icon: Download, label: 'PDF',             desc: 'Mise en page complète, prête à envoyer', color: 'text-orange-400' },
  { id: 'docx',  icon: FileText, label: 'Word (.docx)',    desc: 'Modifiable dans Microsoft Word',          color: 'text-blue-500' },
  { id: 'json',  icon: Code,     label: 'JSON (sauvegarde)', desc: 'Pour sauvegarder et réimporter',       color: 'text-emerald-400' },
  { id: 'print', icon: Printer,  label: 'Imprimer',        desc: 'Ouvrir la boîte de dialogue impression', color: 'text-white/60' },
];

export function ExportModal({
  open,
  onClose,
  data: _data,
  onPrint,
  onExportDocx,
  onExportJson,
  isExporting,
}: ExportModalProps) {
  void _data;

  const handleExport = async (format: ExportFormat) => {
    switch (format) {
      case 'pdf':
        onPrint('graphique');
        onClose();
        break;
      case 'docx':
        await onExportDocx();
        onClose();
        break;
      case 'json':
        onExportJson();
        onClose();
        break;
      case 'print':
        onPrint('graphique');
        onClose();
        break;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exporter le CV"
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-white/60">
          Choisis le format d'export selon ton usage.
        </p>

        {isExporting && (
          <div className="space-y-2 pb-1">
            <p className="text-xs text-white/40">Export en cours…</p>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        )}

        <div className="space-y-2">
          {FORMATS.map(({ id, icon: Icon, label, desc, color }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleExport(id)}
              disabled={isExporting}
              className={cn(
                'flex items-center gap-3 w-full p-3.5 rounded-xl border text-left transition-all',
                'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20',
                'disabled:opacity-40 disabled:cursor-not-allowed',
              )}
            >
              <div className={`p-2 rounded-lg bg-white/[0.04] ${color}`}>
                <Icon className="size-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white/90">{label}</div>
                <div className="text-xs text-white/40">{desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
